import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Sparkles, Zap, Lock } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

export default function ParseJDModal({ open, onOpenChange, onParsed }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  const isPro = user?.tier === "pro";

  const handleParse = async () => {
    if (!isPro) {
      onOpenChange(false);
      navigate("/upgrade");
      return;
    }
    if (text.trim().length < 20) {
      toast.error("Paste a longer job description first.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/ai/parse-jd", { text });
      onParsed?.(data);
      onOpenChange(false);
      setText("");
      toast.success("Extracted — review and save.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-[#FDFCFA]" data-testid="parse-jd-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#1A362D] flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#D99C3D]" />
            Paste job description
          </DialogTitle>
          <DialogDescription>
            Paste the posting and Trackr will fill company, title, pay, and key notes for you.
          </DialogDescription>
        </DialogHeader>

        {!isPro && (
          <div className="rounded-xl bg-[#1A362D] text-[#F4F1EE] p-4 flex items-start gap-3" data-testid="parse-jd-pro-gate">
            <Lock className="w-4 h-4 text-[#D99C3D] shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium">AI JD parsing is a Trackr Pro feature.</p>
              <p className="text-sm opacity-80 mt-0.5">Upgrade to auto-fill applications in one click.</p>
            </div>
          </div>
        )}

        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={10}
          placeholder="Paste the full job description here — LinkedIn, Indeed, careers page, anywhere…"
          className="bg-white mt-2"
          disabled={loading}
          data-testid="parse-jd-textarea"
        />

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-full" data-testid="parse-jd-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleParse}
            disabled={loading}
            className="rounded-full bg-[#1A362D] hover:bg-[#2C4A40] text-white px-6"
            data-testid="parse-jd-submit"
          >
            {loading ? "Extracting…" : isPro ? (<><Sparkles className="w-4 h-4 mr-1.5" /> Extract with AI</>) : (<><Zap className="w-4 h-4 mr-1.5" /> Upgrade to use</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
